<template>
    <v-dialog
        :value="value"
        @input="$emit('input', $event)"
        persistent
        max-width="900"
    >
        <v-card v-if="product" class="add-product-dialog">
            <!-- Header -->
            <v-card-title class="dialog-header">
                <span class="primary_blue--text text-h5 font-weight-bold">Añadir producto</span>
                <v-btn icon @click="$emit('cancel')">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="dialog-content">
                <div class="sections-container">
                    <!-- Sección Izquierda: Información Básica -->
                    <fieldset class="section-fieldset">
                        <legend class="section-legend">Información Básica</legend>

                        <div class="field-group">
                            <label class="field-label">Código del Producto*</label>
                            <v-text-field
                                outlined
                                dense
                                hide-details
                                :value="product.code"
                                @input="updateProduct('code', $event)"
                                class="field-input"
                            ></v-text-field>
                        </div>

                        <div class="field-group">
                            <label class="field-label">Descripción del Producto*</label>
                            <v-textarea
                                outlined
                                dense
                                hide-details
                                :value="product.description"
                                @input="updateProduct('description', $event)"
                                rows="4"
                                placeholder="Descripción del Producto"
                                class="field-input"
                            ></v-textarea>
                        </div>

                        <!-- Campos adicionales (si existen) -->
                        <template v-if="product.additional_fields && product.additional_fields.length">
                            <div class="field-group" v-for="(field, index) in product.additional_fields" :key="index">
                                <label class="field-label">{{ field.name }}</label>
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details
                                    :value="field.value"
                                    @input="updateAdditionalField(index, $event)"
                                    class="field-input"
                                ></v-text-field>
                            </div>
                        </template>
                    </fieldset>

                    <!-- Sección Derecha: Precio e Impuestos -->
                    <fieldset class="section-fieldset">
                        <legend class="section-legend">Precio e Impuestos</legend>

                        <!-- Fila: Precio y Descuento -->
                        <div class="fields-row">
                            <div class="field-group field-half">
                                <label class="field-label">Precio Unitario</label>
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details
                                    :value="product.price"
                                    @input="updateProduct('price', $event)"
                                    @change="$emit('price-change', product)"
                                    type="number"
                                    suffix="€"
                                    class="field-input"
                                ></v-text-field>
                            </div>

                            <div class="field-group field-half">
                                <label class="field-label">Descuento</label>
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details
                                    :value="product.discount || 0"
                                    @input="updateProduct('discount', $event)"
                                    type="number"
                                    class="field-input"
                                ></v-text-field>
                                <div class="discount-options">
                                    <label class="radio-option">
                                        <input
                                            type="radio"
                                            name="discount_type"
                                            value="percentage"
                                            :checked="(product.discount_type || 'percentage') === 'percentage'"
                                            @change="updateProduct('discount_type', 'percentage')"
                                        />
                                        <span>Porcentaje (%)</span>
                                    </label>
                                    <label class="radio-option">
                                        <input
                                            type="radio"
                                            name="discount_type"
                                            value="net"
                                            :checked="product.discount_type === 'net'"
                                            @change="updateProduct('discount_type', 'net')"
                                        />
                                        <span>Importe fijo (€)</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Fila: IVA/IGIC y Retención -->
                        <div class="fields-row">
                            <div class="field-group field-half">
                                <label class="field-label">
                                    {{ taxType.toUpperCase() }}
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small v-bind="attrs" v-on="on" class="help-icon">mdi-help-circle-outline</v-icon>
                                        </template>
                                        <span>Selecciona el tipo de impuesto aplicable</span>
                                    </v-tooltip>
                                </label>
                                <v-autocomplete
                                    autocomplete="off"
                                    outlined
                                    dense
                                    :value="product.selected_tax"
                                    :items="taxItems"
                                    item-text="name"
                                    item-value="value"
                                    :return-object="false"
                                    hide-details
                                    @change="onTaxChange"
                                    class="field-input"
                                    placeholder="Seleccionar"
                                ></v-autocomplete>
                            </div>

                            <div class="field-group field-half">
                                <label class="field-label">
                                    Retención
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small v-bind="attrs" v-on="on" class="help-icon">mdi-help-circle-outline</v-icon>
                                        </template>
                                        <span>Selecciona el porcentaje de retención</span>
                                    </v-tooltip>
                                </label>
                                <v-autocomplete
                                    autocomplete="off"
                                    outlined
                                    dense
                                    :value="product.selected_retention"
                                    :items="lineRetentions"
                                    item-text="name"
                                    item-value="value"
                                    :return-object="false"
                                    hide-details
                                    @change="onRetentionChange"
                                    class="field-input"
                                ></v-autocomplete>
                            </div>
                        </div>

                        <!-- Tipo 0% (solo visible cuando selected_tax == 0) -->
                        <div class="fields-row" v-if="product.selected_tax == 0.0">
                            <div class="field-group field-half">
                                <label class="field-label">Tipo 0%</label>
                                <v-autocomplete
                                    autocomplete="off"
                                    outlined
                                    dense
                                    :value="product.selected_tax_event"
                                    :items="type0Options"
                                    :return-object="false"
                                    hide-details
                                    @change="onTaxEventChange"
                                    class="field-input"
                                ></v-autocomplete>
                            </div>
                            <div class="field-group field-half"></div>
                        </div>

                        <!-- Precio de coste (si feature activa) -->
                        <div class="fields-row" v-if="featureCostPrice">
                            <div class="field-group field-half">
                                <label class="field-label">Precio de coste</label>
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details
                                    :value="product.cost_price"
                                    @input="updateProduct('cost_price', $event)"
                                    type="number"
                                    suffix="€"
                                    class="field-input"
                                ></v-text-field>
                            </div>
                            <div class="field-group field-half"></div>
                        </div>
                    </fieldset>
                </div>
            </v-card-text>

            <!-- Actions -->
            <v-card-actions class="dialog-actions">
                <v-spacer></v-spacer>
                <v-btn
                    outlined
                    @click="$emit('cancel')"
                    class="action-btn cancel-btn"
                >
                    Cancelar
                </v-btn>
                <v-btn
                    color="primary_blue"
                    class="white--text action-btn create-btn"
                    :loading="loading"
                    :disabled="!isValid"
                    @click="$emit('save', product)"
                >
                    Crear Producto
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'AddProductDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        product: {
            type: Object,
            default: null
        },
        taxType: {
            type: String,
            default: 'iva/igic'
        },
        taxItems: {
            type: Array,
            default: () => []
        },
        type0Options: {
            type: Array,
            default: () => ['N/A', 'EX', 'ISP', 'NS', 'INTRA.', 'EXPORT.']
        },
        lineRetentions: {
            type: Array,
            default: () => []
        },
        featureCostPrice: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        allowLinesAtZero: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isValid() {
            if (!this.product) return false;
            const hasCode = this.product.code !== '';
            const hasDescription = this.product.description !== '';
            const hasValidPrice = this.allowLinesAtZero
                ? this.product.price >= 0
                : this.product.price > 0;
            return hasCode && hasDescription && hasValidPrice;
        }
    },
    methods: {
        updateProduct(field, value) {
            this.$emit('update:product', { ...this.product, [field]: value });
        },
        updateAdditionalField(index, value) {
            const updatedFields = [...this.product.additional_fields];
            updatedFields[index] = { ...updatedFields[index], value };
            this.$emit('update:product', { ...this.product, additional_fields: updatedFields });
        },
        onTaxChange(newRate) {
            this.$emit('tax-change', newRate, this.product);
        },
        onTaxEventChange(newRate) {
            this.$emit('tax-event-change', newRate, this.product);
        },
        onRetentionChange(newRate) {
            this.$emit('retention-change', newRate, this.product);
        }
    }
}
</script>

<style scoped>
.add-product-dialog {
    border-radius: 12px !important;
}

.dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px !important;
    border-bottom: none;
}

.dialog-content {
    padding: 0 24px 16px !important;
}

/* Layout de dos columnas */
.sections-container {
    display: flex;
    gap: 24px;
}

.section-fieldset {
    flex: 1;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px 20px;
    margin: 0;
    min-width: 0;
}

.section-legend {
    font-size: 14px;
    font-weight: 500;
    color: #666;
    padding: 0 8px;
}

.field-group {
    margin-bottom: 16px;
}

.field-group:last-child {
    margin-bottom: 0;
}

.field-label {
    display: block;
    font-size: 13px;
    font-weight: 400;
    color: #555;
    margin-bottom: 6px;
}

.help-icon {
    color: #999;
    margin-left: 4px;
    cursor: help;
}

.fields-row {
    display: flex;
    gap: 12px;
}

.field-half {
    flex: 1;
    min-width: 0;
}

/* Opciones de descuento con radio buttons nativos */
.discount-options {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
}

.radio-option {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-size: 12px;
    color: #666;
}

.radio-option input[type="radio"] {
    accent-color: var(--v-primary_blue-base);
    width: 14px;
    height: 14px;
    margin: 0;
}

.dialog-actions {
    padding: 16px 24px 24px !important;
    gap: 12px;
}

.action-btn {
    font-weight: 500;
    font-size: 14px;
    border-radius: 20px;
    padding: 8px 24px !important;
    text-transform: none;
    min-width: 120px;
}

.cancel-btn {
    color: #666 !important;
    border-color: #ccc !important;
}

.create-btn {
    box-shadow: none !important;
}

/* Input styling */
::v-deep .v-text-field--outlined fieldset {
    border-color: #e0e0e0;
}

::v-deep .v-text-field--outlined:hover fieldset {
    border-color: var(--v-primary_blue-base);
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
    border-color: var(--v-primary_blue-base);
}

::v-deep .v-textarea textarea {
    line-height: 1.5;
}

/* Responsive: en pantallas pequeñas apilar verticalmente */
@media (max-width: 768px) {
    .sections-container {
        flex-direction: column;
    }

    .fields-row {
        flex-direction: column;
        gap: 0;
    }

    .field-half {
        width: 100%;
    }
}
</style>
